import React, { useState, useMemo, useEffect } from "react";
import { ArrowRight, ArrowLeft } from "react-bootstrap-icons";
import noDataImg from "../../../assets/img/banner/no-data.png";

const DirectTeamReport = ({ teamData }) => {
  console.log("🔍 [DirectTeamReport] Received teamData:", teamData);
  
  // Transform the direct team data from the nested structure
  const directTeamData = useMemo(() => {
    if (!teamData?.transformedData?.team) {
      return [];
    }

    // Extract level 1 (direct) team members from the new data structure
    const level1Data = teamData.transformedData.team.find(level => level.level === 1);
    
    if (!level1Data?.users) {
      return [];
    }

    const directMembers = level1Data.users.map((user, index) => {
      return {
        idx: index + 1,
        level: 1,
        referral_id: user.referral_id,
        name: user.name,
        email: user.email,
        user_created_on: user.user_created_on ? new Date(user.user_created_on).toLocaleDateString() : 'N/A',
        total_business: user.total_business || 0,
        first_package_activation_date: user.first_package_activation_date === "Not Active" 
          ? "Not Active" 
          : user.first_package_activation_date 
            ? new Date(user.first_package_activation_date).toLocaleDateString()
            : "Not Active",
        active_package_count: user.active_package_count || 0,
        elegibility_diffrence: 0
      };
    });

    return directMembers;
  }, [teamData]);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const handleFilter = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig((prevState) => ({
      key,
      direction:
        prevState.key === key && prevState.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
  };

  const filteredData = useMemo(() => {
    let filtered = directTeamData || [];

    if (searchTerm) {
      filtered = filtered.filter((val) =>
        [
          "referral_id",
          "name",
          "email",
          "total_business",
          "user_created_on",
          "first_package_activation_date"
        ].some((field) =>
          val[field]
            ?.toString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
      );
    }

    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aKey = a[sortConfig.key] || "";
        const bKey = b[sortConfig.key] || "";

        if (aKey < bKey) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aKey > bKey) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [directTeamData, searchTerm, sortConfig]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredData.slice(start, end);
  }, [filteredData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  return (
    <div className="section-padding-medium">
      <div className="card">
        <div className="card-header">
          <div className="card-title bg-dark text-light text-center rounded p-3">
            <h5 className="m-0 me-2">Direct Members Report</h5>
          </div>
        </div>
        <div className="p-3">
          {/* Filter Input */}
          <input
            type="text"
            placeholder="Search by User ID, Name, Email, Date..."
            value={searchTerm}
            onChange={handleFilter}
            className="mb-3 form-control"
          />

          {/* Table Component */}
          <div className="table-responsive">
            {paginatedData.length > 0 ? (
              <table className="table table-dark table-striped">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    <th 
                      scope="col" 
                      onClick={() => handleSort("referral_id")}
                      style={{ cursor: 'pointer' }}
                    >
                      User ID
                      {sortConfig.key === "referral_id" && (
                        <span className="ms-1">
                          {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      scope="col" 
                      onClick={() => handleSort("name")}
                      style={{ cursor: 'pointer' }}
                    >
                      Name
                      {sortConfig.key === "name" && (
                        <span className="ms-1">
                          {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      scope="col" 
                      onClick={() => handleSort("email")}
                      style={{ cursor: 'pointer' }}
                    >
                      Email
                      {sortConfig.key === "email" && (
                        <span className="ms-1">
                          {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      scope="col" 
                      onClick={() => handleSort("total_business")}
                      style={{ cursor: 'pointer' }}
                    >
                      Package Amount
                      {sortConfig.key === "total_business" && (
                        <span className="ms-1">
                          {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      scope="col" 
                      onClick={() => handleSort("user_created_on")}
                      style={{ cursor: 'pointer' }}
                    >
                      Registration Date
                      {sortConfig.key === "user_created_on" && (
                        <span className="ms-1">
                          {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      scope="col" 
                      onClick={() => handleSort("first_package_activation_date")}
                      style={{ cursor: 'pointer' }}
                    >
                      Activation Date
                      {sortConfig.key === "first_package_activation_date" && (
                        <span className="ms-1">
                          {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      scope="col" 
                      onClick={() => handleSort("active_package_count")}
                      style={{ cursor: 'pointer' }}
                    >
                      Active Packages
                      {sortConfig.key === "active_package_count" && (
                        <span className="ms-1">
                          {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((val, index) => (
                    <tr key={val.referral_id || index}>
                      <th scope="row">
                        {index + 1 + (currentPage - 1) * itemsPerPage}
                      </th>
                      <td>{val.referral_id || 'N/A'}</td>
                      <td>{val.name || 'N/A'}</td>
                      <td>{val.email || 'N/A'}</td>
                      <td>${val.total_business?.toFixed(2) || '0.00'}</td>
                      <td>{val.user_created_on || 'N/A'}</td>
                      <td>{val.first_package_activation_date || 'Not Active'}</td>
                      <td>{val.active_package_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data text-center rounded rounded-bottom">
                <img src={noDataImg} alt="No Data" className="img-fluid" />
                <p className="mt-2">
                  {directTeamData.length === 0 
                    ? "No direct team members found" 
                    : "No data matches your search criteria"}
                </p>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {filteredData.length > itemsPerPage && (
            <div className="d-flex justify-content-between p-3">
              <button
                className="btn btn-primary"
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
              >
                <ArrowLeft />
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn btn-primary"
                onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                <ArrowRight />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DirectTeamReport;